import _isEqual from 'lodash/isEqual'
import { useTranslation } from 'next-i18next'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { memo, useState } from 'react'
import { Pressable, Text, View } from 'react-native'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import { Icon } from '@/components/Icon'
import { LanguageSwitcher, useGetNewLocaleFromSwitcher } from '@/components/LanguageSwitcher'
import { PLAUSIBLE_DOMAINS } from '@/config/env'
import type { FooterLinkData } from '@/constants/flows'
import { marketFooterLegalLinks } from '@/constants/flows'
import type { PersonalizationServiceMarketTextsData } from '@/types/flows/market-texts'
import { trpc } from '@/utils/trpc'
import type { SupportedMarketKey } from '@constants/index'
import { getLocaleParts } from '@constants/index'
import { FLOW_HEADER_ID } from '@flows-ui/components/ProgressiveQuestions'
import { StartOverModal } from '@flows-ui/components/StartOverModal'
import { useQuery } from '@tanstack/react-query'
import logo from '@ui-kit/assets/images/src/illustrations/logoOutline_29x24.svg'
import ButtonIcon from '@ui-kit/components/ButtonIcon'
import GlobalHeader from '@ui-kit/components/GlobalHeader'
import PhosphorIcon from '@ui-kit/components/PhosphorIcon'
import { buttonLabel } from '@ui-kit/core/typography'

const flowLayoutStyles = tv({
  slots: {
    wrapper: 'clear-flat-list-z-index flex h-full flex-1 flex-col',
    headerWrapper: 'fixed top-0 z-50 w-full bg-ui-light py-2',
    headerContentsWrapper:
      'order-0 flex flex-row items-center justify-end border-b border-ui-grey-ultra-light lg:order-1 lg:w-auto lg:justify-end lg:border-none lg:px-6',
    phoneNumberWrapper: 'flex items-center',
    phoneNumberLabel: 'hidden font-bold lg:flex',
    childrenContainer: 'flex w-full flex-col items-center bg-ui-light pt-[97px] lg:pt-[116px]',
    childrenWrapper: 'flex w-full flex-col bg-ui-light p-4 pb-36 md:pb-28',
    footerChildrenWrapper: 'w-full max-w-[560px]',
    footerWrapper:
      'fixed bottom-0 flex w-full flex-col items-center justify-center bg-ui-light p-4 md:flex-nowrap',
    footerItemsWrapper:
      'mt-2 flex w-full flex-row flex-wrap items-center justify-center text-xs md:flex-nowrap',
    footerItems:
      "mr-1 cursor-pointer text-xs text-grey-500 underline after:inline-block after:p-1 after:content-['•']",
    proceedButtonWrapper: 'w-full',
    progressBarWrapper: 'w-full',
  },
  variants: {
    nonStickyFooter: {
      true: {
        footerWrapper: 'relative',
        childrenWrapper: 'pb-4',
      },
    },
    withProgressBar: {
      true: {
        childrenContainer: 'pt-[167px]',
      },
    },
    withTopBar: {
      true: {
        headerWrapper: 'pb-0',
        childrenContainer: 'pt-[123px] lg:pt-[152px]',
      },
    },
    alignFooterWithContent: {
      true: { footerItemsWrapper: 'xl:ml-auto xl:mr-[525px] xl:max-w-[600px]' },
    },
    lastFooterItem: {
      true: { footerItems: 'mr-0 after:content-none' },
    },
    newDesign: {
      true: { footerWrapper: 'border-t border-grey-200 px-5 xl:border-t-0 xl:px-4' },
    },
    wideContent: {
      true: {
        childrenWrapper: 'max-w-[1280px] justify-start',
      },
      false: {
        childrenWrapper: 'max-w-[560px]',
      },
    },
    screen: {
      desktop: {
        progressBarWrapper:
          'pointer-events-auto absolute left-1/2 hidden -translate-x-1/2 md:max-w-[300px] lg:flex min-[1080px]:max-w-[560px]',
      },
      mobile: {
        progressBarWrapper: 'mx-auto max-w-[560px] px-4 lg:hidden',
      },
    },
  },
  compoundVariants: [
    {
      newDesign: true,
      wideContent: true,
      class: { footerChildrenWrapper: 'xl:max-w-[1248px]' },
    },
    {
      withProgressBar: true,
      withTopBar: true,
      class: { childrenContainer: 'pt-[193px] lg:pt-[152px]' },
    },
  ],
})

interface Props {
  flowName: string
  children: React.ReactNode
  flowButtons?: React.ReactNode
  progressBar?: React.ReactNode
  flowTitle: string
  alternateLinkPath: string
  firstStepName: string
  stepName: string
  baseLayoutData?: PersonalizationServiceMarketTextsData
  phoneNumbers?: Partial<Record<SupportedMarketKey, string>>
  enableResetSessionModal: boolean
  nonStickyFooter?: boolean
  hideStartOver?: boolean
  wideContent?: boolean
  alignFooterWithContent?: boolean
  newDesign?: boolean
  topBar?: React.ReactNode
}

const readOverrideModalVisibility = () =>
  typeof window !== 'undefined' && localStorage.getItem('gs_overrideModalVisibility')

const getPhoneNumber = (
  phoneNumbers: Props['phoneNumbers'],
  baseLayoutData: Props['baseLayoutData'],
  market: SupportedMarketKey
) => phoneNumbers?.[market] ?? baseLayoutData?.[market]['customerServiceContactNumber'] ?? ''

const isStartOverModalVisible = ({
  hideStartOver,
  hasLeftFirstStep,
  isResetting,
  overrideModalVisibility,
}: {
  hideStartOver?: boolean
  hasLeftFirstStep: boolean
  isResetting: boolean
  overrideModalVisibility: string | false | null
}) => !hideStartOver && hasLeftFirstStep && !isResetting && overrideModalVisibility !== 'true'

const useStartOverModal = ({
  flowName,
  firstStepName,
  stepName,
  enableResetSessionModal,
  hideStartOver,
  locale,
}: Pick<
  Props,
  'flowName' | 'firstStepName' | 'stepName' | 'enableResetSessionModal' | 'hideStartOver'
> & { locale: string | undefined }) => {
  const [overrideModalVisibility, setOverrideModalVisibility] = useState(
    readOverrideModalVisibility
  )
  const resetMutation = trpc.flows.reset.useMutation()

  const loadedPageQuery = useQuery({
    queryKey: ['reset-flow', flowName],
    queryFn: () => {
      localStorage.removeItem('gs_overrideModalVisibility')
      setOverrideModalVisibility(false)

      return firstStepName !== stepName
    },
    enabled: enableResetSessionModal && typeof window !== 'undefined',
  })

  if (resetMutation.isSuccess) {
    // This makes the query re-evaluate only when the URL actually changes and makes sure that the modal is not
    // shown in the first step, otherwise we need to have a lot of checks to make sure that we actually navigated
    window.location.href = `/${locale}/flows/${flowName}`
  }

  return {
    visible: isStartOverModalVisible({
      hideStartOver,
      hasLeftFirstStep: Boolean(loadedPageQuery.data),
      isResetting: resetMutation.isSuccess,
      overrideModalVisibility,
    }),
    onContinue: () => {
      localStorage.setItem('gs_overrideModalVisibility', 'true')
      setOverrideModalVisibility('true')
    },
    onReset: () => {
      resetMutation.mutate(flowName)
    },
  }
}

const ProgressBarSlot = ({
  progressBar,
  screen,
}: {
  progressBar: React.ReactNode
  screen: 'desktop' | 'mobile'
}) =>
  progressBar ? (
    <View className={flowLayoutStyles({ screen }).progressBarWrapper()}>{progressBar}</View>
  ) : null

const getFooterHref = (
  footerLink: FooterLinkData,
  language: ReturnType<typeof getLocaleParts>[0]
) => (typeof footerLink.link === 'string' ? footerLink.link : footerLink.link[language])

const FlowFooterLinks = ({
  links,
  language,
  newDesign,
}: {
  links: FooterLinkData[] | undefined
  language: ReturnType<typeof getLocaleParts>[0]
  newDesign?: boolean
}) => {
  const { t } = useTranslation()
  return (
    <>
      {links?.map((footerLink, index, all) => (
        <Pressable
          onPress={() =>
            window.open(getFooterHref(footerLink, language), '_blank', 'noopener noreferrer')
          }
          key={index}
        >
          <Text
            className={flowLayoutStyles({
              lastFooterItem: newDesign && index === all.length - 1,
            }).footerItems()}
          >
            {t(`${footerLink.label}`)}
          </Text>
        </Pressable>
      ))}
    </>
  )
}

export default memo(
  function BaseFlowLayout({
    children,
    flowButtons,
    progressBar,
    flowTitle,
    alternateLinkPath,
    baseLayoutData,
    phoneNumbers,
    flowName,
    firstStepName,
    stepName,
    enableResetSessionModal,
    nonStickyFooter,
    hideStartOver,
    wideContent,
    alignFooterWithContent,
    newDesign,
    topBar,
  }: Props) {
    const { locale, locales } = useRouter()
    const [language, market] = getLocaleParts(locale)
    const startOverModal = useStartOverModal({
      flowName,
      firstStepName,
      stepName,
      enableResetSessionModal,
      hideStartOver,
      locale,
    })

    const footerData = marketFooterLegalLinks[market]

    const logoHref = `/${locale}`
    const phoneNumber = getPhoneNumber(phoneNumbers, baseLayoutData, market)

    const { newLocale } = useGetNewLocaleFromSwitcher(market, locales, language)

    const ALTERNATE_LINKS = [
      {
        href: `/${newLocale}/flows/${alternateLinkPath}/`,
        hrefLang: newLocale,
      },
    ]

    const {
      headerWrapper,
      headerContentsWrapper,
      phoneNumberWrapper,
      phoneNumberLabel,
      footerWrapper,
      footerChildrenWrapper,
      footerItemsWrapper,
      childrenContainer,
      childrenWrapper,
      proceedButtonWrapper,
      wrapper,
    } = flowLayoutStyles({
      nonStickyFooter,
      withProgressBar: Boolean(progressBar),
      withTopBar: Boolean(topBar),
      wideContent: Boolean(wideContent),
      alignFooterWithContent,
      newDesign,
    })

    return (
      <>
        <Head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta name="robots" content="noindex" />
          <title>{`Getsafe - ${flowTitle}`}</title>
          <script
            defer
            data-domain={PLAUSIBLE_DOMAINS}
            src="https://plausible.io/js/script.js"
          ></script>
        </Head>

        <View className={wrapper()}>
          <View className={headerWrapper()} nativeID={FLOW_HEADER_ID}>
            <GlobalHeader
              logoLink={{
                href: logoHref,
              }}
              logo={
                <Image
                  src={logo}
                  alt="Getsafe logo"
                  unoptimized
                  fill
                  priority
                  loader={({ src }) => src}
                />
              }
              title={flowTitle}
            >
              <ProgressBarSlot progressBar={progressBar} screen="desktop" />
              <View className={headerContentsWrapper()}>
                <LanguageSwitcher alternateLinks={ALTERNATE_LINKS} removeQueryParams />
                <Link href={`tel:${phoneNumber}`} className={phoneNumberWrapper()}>
                  <ButtonIcon
                    icon={
                      newDesign ? (
                        <PhosphorIcon name="Phone" size={24} />
                      ) : (
                        <Icon icon={{ name: 'phone_24' }} size="small" />
                      )
                    }
                  />
                  <Text className={twMerge(buttonLabel(), phoneNumberLabel())}>{phoneNumber}</Text>
                </Link>
              </View>
            </GlobalHeader>
            <ProgressBarSlot progressBar={progressBar} screen="mobile" />
            {topBar}
          </View>
          <View className={childrenContainer()}>
            <View className={childrenWrapper()}>{children}</View>
          </View>

          <View className={footerWrapper()}>
            <View className={footerChildrenWrapper()}>
              <View className={proceedButtonWrapper()}>{flowButtons}</View>
              <View className={footerItemsWrapper()}>
                <FlowFooterLinks links={footerData} language={language} newDesign={newDesign} />
              </View>
            </View>
          </View>
          <StartOverModal
            onContinue={startOverModal.onContinue}
            visible={startOverModal.visible}
            onReset={startOverModal.onReset}
          />
        </View>
      </>
    )
  },
  (prevProps, nextProps) => _isEqual(prevProps, nextProps)
)
